import { Button } from "@/components/ui/button";
import { CalendarDialog } from "@/components/CalendarDialog";
import { ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const TRUST_ITEMS = [
  "Trade & Service Business specialists",
  "More bookings, better visibility, higher prices",
  "For any business doing 20+ jobs/month",
];

export function HeroSection() {
  return (
    <section
      id="hero-section"
      className="relative bg-background text-foreground overflow-hidden"
    >
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 text-center flex flex-col items-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-7 sm:mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-[11px] sm:text-xs font-semibold tracking-wide uppercase text-primary">
            Built for Australian Trade & Service Businesses
          </span>
        </motion.div>

        {/* Headline — rendered immediately for fast LCP, no entrance animation */}
        <h1
          className="text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-foreground leading-[1.1] mb-5 sm:mb-6 max-w-4xl"
        >
          Digital Marketing for Trade & Service Businesses That{" "}
          <span className="text-primary">Actually Works</span>
        </h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed mb-9 sm:mb-10"
        >
          We help electricians, plumbers, builders, cleaners, gyms, salons, pest
          control and more get real results — more local visibility, better
          reviews, higher rankings in local search, and effective ads, all
          working together to grow your business.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 mb-12 sm:mb-14 w-full sm:w-auto"
        >
          <CalendarDialog
            buttonVariant="default"
            buttonSize="lg"
            buttonClassName="w-full sm:w-auto min-w-[260px] h-12 sm:h-[52px] text-[15px] sm:text-base font-bold rounded-xl"
          />
          <Button
            variant="outline"
            size="lg"
            asChild
            className="group w-full sm:w-auto min-w-[200px] h-12 sm:h-[52px] text-[15px] sm:text-base font-semibold rounded-xl"
          >
            <Link to="/services">
              See Our Services
              <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </motion.div>

        {/* Trust row */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 pt-8 border-t border-border/60 w-full max-w-4xl"
        >
          {TRUST_ITEMS.map((item) => (
            <div key={item} className="flex items-start sm:items-center justify-center gap-2.5 text-center sm:text-left">
              <div className="flex-shrink-0 w-5 h-5 mt-0.5 sm:mt-0 rounded-full bg-primary/10 flex items-center justify-center">
                <Check className="w-3 h-3 text-primary" strokeWidth={4} />
              </div>
              <span className="text-[0.8125rem] sm:text-sm font-medium text-muted-foreground leading-snug">
                {item}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
